import React, { useEffect, useState, useRef } from "react";
import Sidebar from "../../components/Sidebar";

import useWebSocket, { ReadyState } from "react-use-websocket";

import { useAuth } from "../../context/authContext";
import api from "../../api";
import CallerStep1 from "../../components/steps/CallerStep1";
import CallerStep2 from "../../components/steps/CallerStep2";
import CallerStep3 from "../../components/steps/CallerStep3";
import CallerList from "../../components/steps/CallerList";

// Circuit breaker para el reconnect del WebSocket: si el socket se abre y se
// cierra demasiadas veces en poco tiempo (loop de reconexión), dejamos de
// reintentar por un rato en vez de martillar al servidor sin parar. Esto es
// necesario porque el contador interno de `react-use-websocket` se resetea
// en cada `onOpen` exitoso, así que su límite de `reconnectAttempts` nunca
// se activa cuando el socket llega a abrirse (aunque sea un instante) antes
// de volver a cerrarse.
const FLAP_WINDOW_MS = 60000;
const FLAP_THRESHOLD = 6;
const FLAP_COOLDOWN_MS = 60000;

const PatientCallerPage = () => {
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const [loading, setLoading] = useState(true);
    const { userData } = useAuth();
    const [medicalOffices, setMedicalOffices] = useState([]);
    const [selectedMedicalOffice, setSelectedMedicalOffice] = useState("");
    const [step, setStep] = useState(1);
    const [connectionStatus, setConnectionStatus] = useState(null);
    const [consultationType, setConsultationType] = useState("general");
    const [connectionError, setConnectionError] = useState(null);
    const reconnectAttempts = useRef(0);
    const pingIntervalRef = useRef(null);
    const openTimestampsRef = useRef([]);
    const [reconnectPaused, setReconnectPaused] = useState(false);
    const reconnectCooldownTimerRef = useRef(null);

    const url = `${process.env.REACT_APP_WS_URL}`;

    useEffect(() => {
        const getMedicalOffices = async () => {
            const res = await api.medicalOffices.getMulti();
            setMedicalOffices(res);
            setLoading(false);
        };
        getMedicalOffices();
    }, []);

    const handleChange = (field, value) => {
        setSelectedMedicalOffice(value);
    };

    const { sendJsonMessage, lastJsonMessage, readyState, getWebSocket } =
        useWebSocket(url, {
            onOpen: () => {
                setConnectionStatus("open");
                setConnectionError(null);
                reconnectAttempts.current = 0;

                // Circuit breaker: si abrimos más de FLAP_THRESHOLD veces en
                // FLAP_WINDOW_MS, es un loop de reconexión (el server no se
                // queda arriba, o algo lo está cerrando enseguida). Frenamos
                // los reintentos por FLAP_COOLDOWN_MS en vez de seguir
                // machacando al servidor sin parar.
                const now = Date.now();
                openTimestampsRef.current = [
                    ...openTimestampsRef.current.filter(
                        (t) => now - t < FLAP_WINDOW_MS,
                    ),
                    now,
                ];
                if (openTimestampsRef.current.length > FLAP_THRESHOLD) {
                    console.error(
                        "WebSocket flapping detectado: se abrió y cerró demasiadas veces en poco tiempo. Pausando la reconexión automática.",
                    );
                    openTimestampsRef.current = [];
                    setConnectionError(
                        "La conexión en tiempo real es inestable. Reintentando en un momento...",
                    );
                    setReconnectPaused(true);
                    clearTimeout(reconnectCooldownTimerRef.current);
                    reconnectCooldownTimerRef.current = setTimeout(() => {
                        setReconnectPaused(false);
                    }, FLAP_COOLDOWN_MS);
                }

                console.log("WebSocket connected");
            },
            onClose: (event) => {
                setConnectionStatus("closed");
                console.log("WebSocket closed:", event.code, event.reason);
                reconnectAttempts.current += 1;
            },
            onError: (error) => {
                console.error("WebSocket error:", error);
                setConnectionError("Error de conexión. Reintentando...");
            },
            shouldReconnect: () => !reconnectPaused,
            reconnectAttempts: 10,
            reconnectInterval: (attemptNumber) =>
                Math.min(1000 * 2 ** attemptNumber, 30000),
            share: true,
        });

    useEffect(() => {
        return () => clearTimeout(reconnectCooldownTimerRef.current);
    }, []);

    // Ping/pong mechanism to detect stale connections
    useEffect(() => {
        if (readyState === ReadyState.OPEN) {
            // Send ping every 45 seconds
            pingIntervalRef.current = setInterval(() => {
                try {
                    sendJsonMessage({ action: "ping" });
                } catch (error) {
                    console.error("Failed to send ping:", error);
                }
            }, 45000);
        }

        return () => {
            if (pingIntervalRef.current) {
                clearInterval(pingIntervalRef.current);
            }
        };
    }, [readyState, sendJsonMessage]);

    // Run when a new WebSocket message is received (lastJsonMessage)
    useEffect(() => {
        if (!lastJsonMessage) return;

        if (
            lastJsonMessage?.status === "success" &&
            lastJsonMessage?.type === "connection" &&
            step !== 2
        ) {
            setStep(2);
        } else if (
            lastJsonMessage?.type === "no_patient" &&
            lastJsonMessage?.status === "pass"
        ) {
            setStep(3);
        } else if (
            lastJsonMessage?.type === "heartbeat" ||
            lastJsonMessage?.type === "pong"
        ) {
            // Connection is alive, ignore these messages
            return;
        } else if (lastJsonMessage?.status === "error") {
            console.error("WebSocket error message:", lastJsonMessage.message);
            setConnectionError(lastJsonMessage.message);
        }
    }, [lastJsonMessage, step]);

    const connectQueue = () => {
        if (readyState === ReadyState.OPEN) {
            if (consultationType === "general") {
                sendJsonMessage(
                    {
                        action: "join_general_queue_group",
                    },
                    false,
                );
            } else {
                sendJsonMessage(
                    {
                        action: "join_medical_office_group",
                        data: {
                            medical_office_id: selectedMedicalOffice,
                            doctor_id: userData.id,
                        },
                    },
                    false,
                );
            }
        }
    };

    const getStep = () => {
        if (loading) {
            return (
                <div className="flex h-full items-center justify-center ">
                    <div className="w-16 h-16 border-b-2 border-blue-900 rounded-full animate-spin"></div>
                </div>
            );
        }
        if (step === 1) {
            return (
                <CallerStep1
                    userData={userData}
                    selectedMedicalOffice={selectedMedicalOffice}
                    handleChange={handleChange}
                    loading={loading}
                    medicalOffices={medicalOffices}
                    connectQueue={connectQueue}
                    setConsultationType={setConsultationType}
                />
            );
        } else if (step === 2 && consultationType === "general") {
            return (
                <CallerStep2
                    userData={userData}
                    sendJsonMessage={sendJsonMessage}
                    lastJsonMessage={lastJsonMessage}
                    selectedMedicalOffice={selectedMedicalOffice}
                    setStep={setStep}
                    consultationType={consultationType}
                />
            );
        } else if (step === 2 && consultationType !== "general") {
            return (
                <CallerList
                    userData={userData}
                    sendJsonMessage={sendJsonMessage}
                    lastJsonMessage={lastJsonMessage}
                    selectedMedicalOffice={selectedMedicalOffice}
                    setStep={setStep}
                    consultationType={consultationType}
                />
            );
        } else if (step === 3) {
            return <CallerStep3 />;
        }
    };

    return (
        <div className="bg-gray-100 h-screen">
            <Sidebar
                currentScreen={"Turnero"}
                sidebarOpen={sidebarOpen}
                setSidebarOpen={setSidebarOpen}
            />
            <div className="md:pl-64 flex flex-col flex-1">
                {/* Connection status indicator */}
                {readyState !== ReadyState.OPEN && (
                    <div
                        className="bg-yellow-100 border-l-4 border-yellow-500 text-yellow-700 p-4"
                        role="alert"
                    >
                        <p className="font-bold">Estado de conexión</p>
                        <p>
                            {readyState === ReadyState.CONNECTING &&
                                "Conectando..."}
                            {readyState === ReadyState.CLOSING &&
                                "Cerrando conexión..."}
                            {readyState === ReadyState.CLOSED &&
                                "Desconectado. Reintentando..."}
                        </p>
                    </div>
                )}
                {connectionError && (
                    <div
                        className="bg-red-100 border-l-4 border-red-500 text-red-700 p-4"
                        role="alert"
                    >
                        <p className="font-bold">Error</p>
                        <p>{connectionError}</p>
                    </div>
                )}
                <div className="grid h-screen place-items-center">
                    {getStep()}
                </div>
            </div>
        </div>
    );
};

export default PatientCallerPage;
